import * as React from 'react';

import debounce from 'debounce';

import { AppServicesContext } from '@kamernet/core/AppServices';
import { reportError } from '@kamernet/core/Errors';
import { KamernetError } from '@kamernet/core/ApiClient';
import {
  FindMapListingPayload,
  FindMapListingResponse,
  MapMarker,
  OpResponseCode,
} from '@kamernet/core/ApiClient/Kamernet';

import { ListingsContext } from '@kamernet/modules/Listing/context/ListingsContext';
import { ListingActionTypes } from '@kamernet/modules/Listing/context/types';

import { tranformMapListingToRegularListing } from '../pages/Listings/components/Map/Utils';

const DEBOUNCE_MS = 1000;
export const useMapListingDispatcher = () => {
  const { dispatch } = ListingsContext.useContext();

  const { apiClient } = AppServicesContext.useContext();
  const requestIdRef = React.useRef<number | undefined>(undefined);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const mapListingStateDispatcher = React.useMemo(
    () =>
      debounce(async (mapData: MapMarker[]) => {
        const currentRequestId = Date.now();
        requestIdRef.current = currentRequestId;
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        if (mapData && mapData.length) {
          const mapListingRequestPayload: FindMapListingPayload = {
            PostalCodePoints: mapData || [],
          };
          dispatch({
            type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
            payload: true,
          });
          await apiClient.listing
            .findMapListings(
              {
                findMapListingPayload: mapListingRequestPayload,
              },
              {
                signal: abortController.signal,
              },
            )
            .then((response: FindMapListingResponse) => {
              if (
                response &&
                response.OpResponse.Code === OpResponseCode.OPERATION_SUCCESSFUL
              ) {
                if (currentRequestId !== requestIdRef.current) {
                  return;
                }
                dispatch({
                  type: ListingActionTypes.UPDATE_LISTING,
                  payload: tranformMapListingToRegularListing(response),
                });
                dispatch({
                  type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
                  payload: false,
                });
              }
            })
            .catch((error: unknown | KamernetError) => {
              dispatch({
                type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
                payload: false,
              });
              reportError(error as Error, {
                extra: {
                  context: 'Updating map listings failed',
                  metaData: { error },
                },
              });
            })
            .finally(() => {
              dispatch({
                type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
                payload: false,
              });
            });
        }
      }, DEBOUNCE_MS),
    [apiClient.listing, dispatch],
  );

  return { mapListingStateDispatcher };
};
